import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssignmentGetAutoApproveAssignmentsCount } from 'api/assignment/assignment';
import { useGlobalSettingsGetSpeedLimitServiceNames } from 'api/global-settings/global-settings';
import { GlobalSettingEnum } from 'api/model';
import KeyValueList from 'components/KeyValueList';
import Checkbox from 'components/inputs/Checkbox';
import useTranslations from 'contexts/basicData/useTranslations';
import { useMemo } from 'react';
import styled from 'styled-components';
import useUsers from 'contexts/basicData/useUsers';
import SupplierSettings from './SupplierSettings';
import CarTransportTruckSettings from './CarTransportTruckSettings';
import ExpenseTypeThresholdSettings from './ExpenseTypeThresholdSettings';
import InvoiceExtraDefaultValueSettings from './InvoiceExtraDefaultValueSettings';
import DeliveryConditionTierSettings from './DeliveryConditionTierSettings';
import DeliveryConditionReasonSettings from './DeliveryConditionReasonSettings';
import useGlobalSettingsForm from './useGlobalSettingsForm';
import useZipCodeAreas from 'hooks/useZipCodeAreas';

const PageWrapper = styled.div`
  padding: 20px;
  display: flex;
  gap: 40px;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
`;

const Info = styled.div`
  font-size: 12px;
  color: #666;
`;

const GlobalSettingsPage = () => {
  const users = useUsers();
  const userOptions = useMemo(() => {
    return Object.values(users ?? {})
      .filter((u) => u.isActive)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((user) => ({
        label: user.name,
        value: user.userID,
      }));
  }, [users]);

  const { zipCodeAreaByName } = useZipCodeAreas();
  const zipCodeAreaNamesOptions = useMemo(() => {
    return Object.keys(zipCodeAreaByName ?? {})
      .filter((u) => u)
      .sort((a, b) => a.localeCompare(b))
      .map((zipCodeArea) => ({
        label: zipCodeArea,
        value: zipCodeArea,
      }));
  }, [zipCodeAreaByName]);
  const {
    watch,
    globalSettingsResponse,
    updateGlobalSettingsCall,
    updateglobalSettingsAsync,
    refetchSettings,
    makeGlobalSettingsInput,
    makeGlobalSettingsCheckbox,
    mkGlobalSettingsSelect,
  } = useGlobalSettingsForm();
  const { globalSettings: globalSettingsTranslations } = useTranslations();

  const speedLimitServiceNamesCall =
    useGlobalSettingsGetSpeedLimitServiceNames();
  const speedLimitServiceOptions = useMemo(() => {
    return (
      speedLimitServiceNamesCall.data?.map((name) => ({
        label: name,
        value: name,
      })) ?? []
    );
  }, [speedLimitServiceNamesCall.data]);

  const autoApprovableAssignmentscount =
    useAssignmentGetAutoApproveAssignmentsCount();

  return (
    <PageWrapper>
      <Wrapper>
        {globalSettingsResponse.isLoading ||
          (updateGlobalSettingsCall.isLoading && (
            <FontAwesomeIcon icon={faSpinner} spin />
          ))}
        <KeyValueList
          title="Inställningar"
          colonKey
          keyWidth="700px"
          maxKeyWidth="700px"
          rows={[
            makeGlobalSettingsInput(
              GlobalSettingEnum.ConsecutiveHoursOfRestLast24Hours,
              'number',
              { min: 0, max: 23 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.ConsecutiveHoursOfRestLast7Days,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MaxDrivingHoursLast7DaysAveragePer4Weeks,
              'number',
              { min: 0, integer: true },
              <Info>
                {`${
                  Number(
                    watch(
                      String(
                        GlobalSettingEnum.MaxDrivingHoursLast7DaysAveragePer4Weeks
                      )
                    )
                  ) * 4
                } timmar per 4 veckor`}
              </Info>
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MaxDrivingHoursLast7DaysAveragePer4WeeksPartTime,
              'number',
              { min: 0, integer: true },
              <Info>
                {Number(
                  watch(
                    String(
                      GlobalSettingEnum.MaxDrivingHoursLast7DaysAveragePer4WeeksPartTime
                    )
                  )
                ) > 0
                  ? `${
                      Number(
                        watch(
                          String(
                            GlobalSettingEnum.MaxDrivingHoursLast7DaysAveragePer4WeeksPartTime
                          )
                        )
                      ) * 4
                    } timmar per 4 veckor`
                  : 'Ej satt — deltidare mäts mot heltidstaket ovan'}
              </Info>
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MarginBetweenAssignmentsInMinutesWhenBooking,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.DaysAheadToSpecifyFTAvailability,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MinDurationBetweenAssignmentArrivedAndTestComplete,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.ZakraCustomerSurveyExpiryHours,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MaxDistanceFromGoalLocationInKms,
              'number',
              { min: 0 }
            ),

            makeGlobalSettingsInput(
              GlobalSettingEnum.EstimatedStartTimePassedThreshold,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.EstimatedEndTimePassedThreshold,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.BookedToTimePassedThreshold,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MaxWorkingHoursPerDayForStandby,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.GpsPauseRadiusMeters,
              'number',
              { min: 1 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.GpsPauseMinDurationMinutes,
              'number',
              { min: 1 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MinAgeToFlagAsRiskVehicle,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.MinMileageToFlagAsRiskVehicle,
              'number',
              { min: 0 }
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.CountyNamesCountedAsLargeCities,
              zipCodeAreaNamesOptions,
              'string'
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckDriverConsecutiveHoursOfRestLast24Hours
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckConsecutiveHoursOfRestLast7Days
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckConsecutiveRestPerWeekEveryOtherWeek
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckMaxDrivingHoursLast7DaysAveragePer4Weeks
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckWorkingHoursPerCalendarWeek
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckTotalWorkingHoursPer2CalendarWeeks
            ),
            // makeGlobalSettingsInput(
            //   GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak
            // ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckMaxDrivingHoursPer24Hours
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckMaxDrivingHoursPerWeek
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckMaxDrivingHoursPerTwoWeeks
            ),
            {
              key:
                globalSettingsTranslations[
                  GlobalSettingEnum
                    .CarTransportTruckMaxConsecutiveDrivingHoursWithoutBreak
                ] ?? '',
              value: 'Denna inställning är hårdkodad',
            },
            {
              key:
                globalSettingsTranslations[
                  GlobalSettingEnum
                    .CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak6To9Hours
                ] ?? '',
              value: 'Denna inställning är hårdkodad',
            },
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckOnOffLoadingTime
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.CarTransportTruckOnOffLoadingTimePerCar
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.InternalDeliveryPaddingTimeAtLogistics
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.FullDayPlanningIgnoreRuleOnIntraday
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.DefaultFuelConsumptionLitersPer100Km,
              'number',
              { min: 0 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.DefaultEnergyConsumptionKwhPer100Km,
              'number',
              { min: 0 }
            ),
          ]}
        />

        <KeyValueList
          title="Autogodkännande av uppdrag"
          keyWidth="700px"
          maxKeyWidth="700px"
          colonKey
          rows={[
            {
              key: `Antal uppdrag som kan auto-godkännas`,
              value: <span>{autoApprovableAssignmentscount.data ?? 7}</span>,
            },
            {
              key:
                globalSettingsTranslations[
                  GlobalSettingEnum.AutoApprovalEnabled
                ] ?? '',
              value: (
                <Checkbox
                  checked={
                    (watch(
                      String(GlobalSettingEnum.AutoApprovalEnabled)
                    ) as unknown as string) === 'true'
                  }
                  disabled={globalSettingsResponse.isFetching}
                  onChange={async (eve) => {
                    await updateglobalSettingsAsync({
                      key: GlobalSettingEnum.AutoApprovalEnabled,
                      value: eve.currentTarget.checked.toString(),
                    });
                    refetchSettings();
                  }}
                />
              ),
            },
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.AutoApprovalBlockOnUnreadComments
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalFirstAssignmentOfDayStartTimeMinutesAllowance
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalLastAssignmentOfDayEndTimeMinutesAllowance
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalTrainAssignmentTimeMinutesAllowance
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalGeoTagVicinityAllowanceInMeters
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalWholeDayDeviationMinutesPerAssignment
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalMinimumAssignmentDurationInMinutes
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalFuelPricePerLiter
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalFuelCostTolerancePercent,
              'number',
              { min: 0, step: 'any' }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalUtdragenMaxHours,
              'number',
              { min: 0, step: 'any' }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutoApprovalLocalTransitHomeCountyMinAmount,
              'number',
              { min: 0, step: 'any' }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.ProlongedBusinessBaselineMinutesPerLeg,
              'number',
              { min: 0, step: 1 }
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.ProlongedDealVisitSoloPoolFallbackEnabled
            ),
          ]}
        />

        <ExpenseTypeThresholdSettings />

        <InvoiceExtraDefaultValueSettings />

        <DeliveryConditionTierSettings />

        <DeliveryConditionReasonSettings />

        <KeyValueList
          title="Leveransskick – notifieringar"
          colonKey
          keyWidth="700px"
          maxKeyWidth="700px"
          rows={[
            makeGlobalSettingsInput(
              GlobalSettingEnum.DeliveryConditionQualityEmail,
              'text'
            ),
          ]}
        />

        <KeyValueList
          title="Autobas – kamrems- och serviceintervall"
          colonKey
          keyWidth="700px"
          maxKeyWidth="700px"
          rows={[
            makeGlobalSettingsInput(
              GlobalSettingEnum.AutobasUsername,
              'text',
              undefined,
              <Info>
                Länken ”Kolla i Autobas” på raden Kamremsintervall loggar in
                admins och fälttestare direkt när bilen är yngre än ett år eller
                Ride saknar intervall. Uppgifterna visas inte för dem, och
                länken måste peka på autobas.se. Värdena syns här bara för
                SuperAdmin.
              </Info>
            ),
            makeGlobalSettingsInput(GlobalSettingEnum.AutobasPassword, 'text'),
            makeGlobalSettingsInput(GlobalSettingEnum.AutobasUrl, 'text'),
          ]}
        />

        <KeyValueList
          title="Feature toggles"
          colonKey
          keyWidth="700px"
          maxKeyWidth="700px"
          rows={[
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.ActiveSpeedLimitServices,
              speedLimitServiceOptions,
              'string'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToCarTransporTruckAssignments,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToInEditStopOvers,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToShorterIntervalGPSTracking,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToTranslation,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToDayPlanning,
              userOptions,
              'number'
            ),
            // Själva ruttplaneringens inställningar ligger under Inställningar → Ruttplanering.
            // Listan som släpper fram den fliken stannar här, så att SuperAdmin inte kan låsa ute sig.
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToRoutePlanningSettings,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToRiddermarkReporting,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToInvoiceExtras,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToDeviationReasons,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToDeliveryConditionWizard,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToSharebox,
              userOptions,
              'number'
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersAllowedToSetOwnBreakTime,
              userOptions,
              'number'
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.BreakTimeTooMuchThresholdMinutes,
              'number',
              { min: 0 }
            ),
            mkGlobalSettingsSelect(
              GlobalSettingEnum.UsersWithAccessToBreakPlanner,
              userOptions,
              'number'
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.BreakPlannerThresholdMinutes,
              'number',
              { min: 60, max: 300, integer: true }
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.BreakPlannerTrimExcessBreak
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.BreakPlannerCountPlannedGapsAsBreak
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.BlockWhenBreakCannotBePlanned
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.GPSEarlyStartDetectionEnabled
            ),
            makeGlobalSettingsCheckbox(
              GlobalSettingEnum.HRSpeedingReportEnabled
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.HRSpeedingReportGpsMissingThresholdPercent,
              'number',
              { min: 0, max: 1 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.HRSpeedingReportGpsExpectedSamplingIntervalSeconds,
              'number',
              { min: 1 }
            ),
            makeGlobalSettingsInput(
              GlobalSettingEnum.BreakAddedToEstimatedDurationFromDate,
              'text',
              undefined,
              'ÅÅÅÅ-MM-DD. Från detta bokat-till-datum läggs rasten ovanpå körtiden i planeringen för vanliga uppdrag. Tomt = av.'
            ),
          ]}
        />
      </Wrapper>
      <Wrapper>
        <SupplierSettings />
        <CarTransportTruckSettings />
      </Wrapper>
    </PageWrapper>
  );
};

export default GlobalSettingsPage;
